import { type Dtos, type SuretyAccountId } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../utils/request";
import { balanceSheetsQueryKey } from "./financials.keys";
import { patchBalanceSheetView } from "./utils/patchBalanceSheetView";

export function useDeleteBalanceSheetLineItems(accountId: SuretyAccountId) {
	const { _delete } = useRequest();
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationKey: ["deleteBalanceSheetLineItems", accountId],
		mutationFn: (body: Dtos.Admin.BalanceSheet.BulkDelete.Request) =>
			_delete(`/v2/admin/surety/accounts/${accountId}/balance-sheets/line-items`, body, "none"),
		onMutate: async (body) => {
			await queryClient.cancelQueries({ queryKey: balanceSheetsQueryKey(accountId) });
			const previousData = queryClient.getQueryData<Dtos.Admin.BalanceSheet.List.Response>(
				balanceSheetsQueryKey(accountId),
			);
			const patchBody: Dtos.Admin.BalanceSheet.Update.Request = {
				sectionPath: body.sectionPath,
				label: body.label,
				values: { value: null },
			};
			queryClient.setQueryData<Dtos.Admin.BalanceSheet.List.Response>(
				balanceSheetsQueryKey(accountId),
				(cached) =>
					cached?.map((sheet) =>
						body.balanceSheetIds.includes(sheet.id) && sheet.view
							? { ...sheet, view: patchBalanceSheetView(sheet.view, patchBody) }
							: sheet,
					),
			);
			return { previousData };
		},
		onError: (_error, _vars, context) => {
			if (context?.previousData) {
				queryClient.setQueryData(balanceSheetsQueryKey(accountId), context.previousData);
			}
		},
		onSettled: () => {
			if (
				queryClient.isMutating({ mutationKey: ["deleteBalanceSheetLineItems", accountId] }) <= 1
			) {
				queryClient.invalidateQueries({ queryKey: balanceSheetsQueryKey(accountId) });
			}
		},
	});

	return {
		deleteBalanceSheetLineItems: mutation.mutateAsync,
	};
}
